/**
 * Bundle 153a - public intro suggestion confirmation.
 *
 * Route: /intro/suggestion/:token
 *
 * Reporter lands here from the tpl_intro_target_suggestion_v1
 * email. Renders masked target initials and Yes/No buttons.
 * No auth required; token is JWT-signed with 14d TTL.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const IntroSuggestionConfirmPage = () => {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // 'yes' | 'no' | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/intros/suggestion/${token}`);
      setCtx(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'This link is invalid or expired.');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const answer = async (yes) => {
    setBusy(true);
    try {
      const url = yes
        ? `${API}/api/intros/confirm-suggestion`
        : `${API}/api/intros/reject-suggestion`;
      const { data } = await axios.post(url, { token });
      setDone(yes ? 'yes' : 'no');
      toast.success(yes
        ? 'Thanks for confirming. Your matchmaker will take it from here.'
        : 'Got it. Your matchmaker will suggest another candidate.');
      setCtx({ ...ctx, status: data.status });
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not record your answer.');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
      </div>
    );
  }

  if (!ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <Card className="max-w-md w-full shadow-soft">
          <CardContent className="p-6 text-center">
            <h1 className="font-display text-xl text-primary-900 mb-2">
              Link invalid
            </h1>
            <p className="text-sm text-neutral-600">
              This confirmation link has expired or is not recognized.
              If you need help, write to yaar@dostination.com.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const initials = [
    ctx.target_first_initial, ctx.target_last_initial,
  ].filter(Boolean).join(' ') || 'the person you named';

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
      <Card className="max-w-lg w-full shadow-soft" data-testid="intro-suggestion-page">
        <CardContent className="p-8 space-y-5">
          <div className="text-xs uppercase tracking-widest text-secondary-700">
            {ctx.event_title || 'Dostination event'}
          </div>
          <h1 className="font-display text-2xl text-primary-900">
            Is this the person you meant?
          </h1>
          <p className="text-neutral-700 leading-relaxed">
            Based on your note, your matchmaker thinks you might
            have meant <strong>{initials}</strong>. Before we
            reach out to them, we need you to confirm.
          </p>

          {ctx.already_answered || done ? (
            <div className="rounded-lg border bg-primary-50 border-primary-200 p-4 text-primary-900 text-sm" data-testid="intro-suggestion-already-answered">
              We&apos;ve recorded your answer. Your matchmaker will
              follow up shortly.
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => answer(true)}
                disabled={busy}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="intro-suggestion-yes-btn"
              >
                Yes, that is who I meant
              </Button>
              <Button
                onClick={() => answer(false)}
                disabled={busy}
                variant="outline"
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                data-testid="intro-suggestion-no-btn"
              >
                No, different person
              </Button>
            </div>
          )}

          <div className="text-[11px] text-neutral-500 pt-2">
            Your matchmaker only sees your answer. We never share
            your details with the other person until both of you
            confirm.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntroSuggestionConfirmPage;
